import { create } from "zustand";
import { ANNOUNCEMENT_PROP } from "../optics/components/drawers/settings/features/announcements/defaults";

export const useAnnouncementsStore = create((set, get) => ({
  data: [],
  updateData: (value) => {
    get().pushToHistory({
      data: value,
      config: { ...get().config },
    });

    set({ data: value });
  },

  getData: () => ({ data: get().data, config: get().config }),
  setData: ({ data, config }) => set({ data, config }),

  handleClickOnTemplate: (template) => {
    const { data, config, selectedAnnouncement } = get();
    const { announcement: templateData, config: templateConfig } = template;

    const newConfig = { ...(config || {}), ...templateConfig };
    set(() => ({ config: { ...newConfig } }));

    const newData = data?.length
      ? data.map((item) => ({
          ...templateData,
          id: item.id,
          date: item.date,
          title: item.title,
          text: item.text,
          linkText: item.linkText,
          location: item.location,
          icon: item.icon,
          url: item.url,
          time: item.time,
          newTab: item.newTab,
          emoji: item.emoji,
          image: item.image,
        }))
      : [templateData];

    const newSelectedAnnouncement = selectedAnnouncement
      ? newData.find((item) => item.id === selectedAnnouncement)
      : undefined;

    get().pushToHistory({
      data: newData,
      config: newConfig,
    });

    set(() => ({
      data: newData,
      selectedAnnouncement: newSelectedAnnouncement?.id,
    }));
  },

  updateAnnouncementsColors: (value) => {
    const data = get().data.map((item) => ({
      ...item,
      color: value.accent,
      textColor: value.text,
    }));
    get().pushToHistory({
      data,
      config: { ...get().config },
    });
    set({ data });
  },

  config: {},
  setConfig: (config) => set({ config }),
  updateConfig: ({ key, value }) => {
    const config = { ...get().config, [key]: value };
    if (key === "columns") config.view = value === 1 ? "list" : "card";

    get().pushToHistory({
      data: get().data,
      config: { ...get().config, ...config },
    });

    set((state) => ({
      config: { ...state.config, ...config },
    }));
  },

  selectedAnnouncement: undefined,
  setSelectedAnnouncement: (id) => set({ selectedAnnouncement: id }),

  hoveredAnnouncement: undefined,
  setHoveredAnnouncement: (id) => set({ hoveredAnnouncement: id }),

  updateAnnouncement: ({ key, value }) => {
    const id = get().selectedAnnouncement;
    const data = get().data.map((item) =>
      item.id === id ? { ...item, [key]: value } : item
    );

    get().pushToHistory({
      data,
      config: { ...get().config },
    });
    set((state) => ({
      data,
    }));
  },

  updateSingleAnnouncementColor: ({ accent, text }) => {
    const id = get().selectedAnnouncement;
    const data = get().data.map((item) =>
      item.id === id
        ? { ...item, color: { ...accent }, textColor: { ...text } }
        : item
    );
    get().pushToHistory({
      data,
      config: { ...get().config },
    });
    set((state) => ({
      data,
    }));
  },

  removeAnnouncement: (id) => {
    const data = get().data.filter((item) => item.id !== id);
    const selectedAnnouncement = get().selectedAnnouncement;
    const hoveredAnnouncement = get().hoveredAnnouncement;

    get().pushToHistory({
      data,
      config: { ...get().config },
    });

    set(() => ({
      data,
      selectedAnnouncement:
        selectedAnnouncement === id ? undefined : selectedAnnouncement,
      hoveredAnnouncement:
        hoveredAnnouncement === id ? undefined : hoveredAnnouncement,
    }));
  },

  moveAnnouncement: (id, direction) => {
    const index = get().data.findIndex((item) => item.id === id);
    if (index === -1) return;

    let newIndex = direction === "left" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= get().data.length) return;

    const updatedData = [...get().data];
    const [movedAnnouncement] = updatedData.splice(index, 1);
    updatedData.splice(newIndex, 0, movedAnnouncement);

    get().pushToHistory({
      data: updatedData,
      config: { ...get().config },
    });

    set(() => ({
      data: updatedData,
    }));
  },

  updateAnnouncementsIcon: ({ value, type }) => {
    const id = get().selectedAnnouncement;
    const data = get().data.map((item) =>
      item.id === id
        ? {
            ...item,
            [type === "icon"
              ? ANNOUNCEMENT_PROP.ICON
              : ANNOUNCEMENT_PROP.EMOJI]: value,
            [ANNOUNCEMENT_PROP.EMOJI_ENABLED]: type === "icon" ? false : true,
          }
        : item
    );

    get().pushToHistory({
      data,
      config: { ...get().config },
    });

    set(() => ({
      data,
    }));
  },

  // undo redo
  position: 0,
  updatePosition: (value) => {
    set(() => ({
      position: value,
    }));
  },

  history: [],
  pushToHistory: (value) => {
    const newHistory = [...get().history, value];

    set(() => ({
      history: newHistory,
      position: newHistory.length - 1,
    }));
  },

  undo: () => {
    const { history, position, setData } = get();
    const { config: newConfig, data: newAnnouncements } = history[position - 1];
    setData({ config: newConfig, data: newAnnouncements });
    set({ position: position - 1 });
  },
  redo: () => {
    const { history, position, setData } = get();
    const { config: newConfig, data: newAnnouncements } = history[position + 1];
    setData({ config: newConfig, data: newAnnouncements });
    set({ position: position + 1 });
  },

  canUndo: () => {
    const { position } = get();
    return position > 0;
  },
  canRedo: () => {
    const { history, position } = get();
    return position < history.length - 1;
  },

  resetStore: () => {
    set({
      data: [],
      config: undefined,
      selectedAnnouncement: undefined,
      hoveredAnnouncement: undefined,
      position: 0,
      history: [],
    });
  },
}));
